import { useCurrentUser } from '@/affiliate-dashboard/context/current-user-context';
import {
  type MemberExtraFragment,
  useGetSubaffiliatesQuery,
} from '@/affiliate-dashboard/graphql';
import { Label } from '@/components/base/label';
import { Loading } from '@/components/base/loading';
import Section from '@/components/base/section';
import { ErrorPage } from '@/components/error-page';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motionVariants } from './animation';
import { Breadcrumbs } from './breadcrumbs';
import { ManageAffiliateCode } from './manage-affiliate-code';
import { Member } from './member';

function SubaffiliatesComponent(): JSX.Element {
  const { t } = useTranslation();
  const { me } = useCurrentUser();
  const [parentId, setParentId] = useState<string | null>(me.id);
  const [breadcrumbs, setBreadcrumbs] = useState<MemberExtraFragment[]>([
    {
      ...me,
      __typename: 'Member',
      initials: `${me.firstName[0]}${me.lastName[0]}`,
    },
  ]);

  type AnimationDirection = 'forward' | 'backward';
  const [animationDirection, setAnimationDirection] =
    useState<AnimationDirection>('forward');
  const [animationKey, setAnimationKey] = useState(0);
  const breadcrumbsLength = breadcrumbs.length;
  const parentSubaffiliate = breadcrumbs[breadcrumbsLength - 1];

  const { data, loading, error } = useGetSubaffiliatesQuery({
    variables: { userId: parentId },
    skip: parentId === null,
  });

  const handleSubaffiliateClick = (sub: MemberExtraFragment | null) => {
    setAnimationDirection('forward');
    setParentId(sub?.id ?? '');
    // @ts-expect-error -- ok
    setBreadcrumbs((prev) => [...prev, sub]);
    setAnimationKey((prevKey) => prevKey + 1);
  };

  const handleBreadcrumbClick = (index: number) => {
    setAnimationDirection('backward');
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(newBreadcrumbs);
    setParentId(newBreadcrumbs[index].id ?? '');
    setAnimationKey((prevKey) => prevKey + 1);
  };

  const handleClickBack = () => {
    const i = breadcrumbsLength - 2;
    setAnimationDirection('backward');
    const newBreadcrumbs = breadcrumbs.slice(0, i + 1);
    setBreadcrumbs(newBreadcrumbs);
    setParentId(newBreadcrumbs[i].id ?? '');
    setAnimationKey((prevKey) => prevKey + 1);
  };

  if (error) return <ErrorPage />;

  const subaffiliates = data?.subAffiliates?.members ?? [];

  const sortedMembers = [...(subaffiliates ?? [])].sort(
    (a, b) =>
      (b?.monthlyRevenue ?? 0) - (a?.monthlyRevenue ?? 0) ||
      (b?.totalReferrals ?? 0) - (a?.totalReferrals ?? 0),
  );

  return (
    <div className="flex flex-col gap-3">
      <ManageAffiliateCode refcode={me?.refcode ?? ''} />

      <div className="flex flex-wrap gap-2">
        <div className="flex gap-3 rounded-md">
          <Breadcrumbs
            breadcrumbs={breadcrumbs}
            onClick={handleBreadcrumbClick}
          />
        </div>
      </div>
      <Section variant="secondary">
        <div className="flex w-full flex-col">
          <div className="flex w-full items-center gap-2 border-b border-default-gray-700 py-2 dark:border-default-gray-800">
            <div className="flex flex-wrap gap-2">
              {breadcrumbsLength > 1 && (
                <button
                  type="button"
                  onClick={() => {
                    handleClickBack();
                  }}
                >
                  <Label
                    variant="gray"
                    className="flex items-center gap-1 rounded-xl bg-[#282C38]/50 px-4 py-2 text-sm text-white dark:bg-[#282C38] dark:text-white"
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                    {t('referrals.back')}
                  </Label>
                </button>
              )}
            </div>

            <div className="flex w-full items-center gap-2 transition-all">
              {t('referrals.generationLevel')}
              <motion.span
                key={breadcrumbsLength}
                initial={motionVariants.appear.initial}
                animate={motionVariants.appear.animate}
                exit={motionVariants.appear.exit}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              >
                {breadcrumbsLength}
              </motion.span>
            </div>
          </div>

          <div className="relative mt-5 flex flex-col">
            <Member
              level={1}
              subAffiliate={parentSubaffiliate}
              className="cursor-default hover:bg-transparent"
            />
            {loading ? (
              <Loading />
            ) : (
              <motion.div
                key={animationKey}
                initial={motionVariants[animationDirection].initial}
                animate={motionVariants[animationDirection].animate}
                exit={motionVariants[animationDirection].exit}
                transition={{ type: 'tween', duration: 0.3 }}
                className="flex w-full flex-col pl-2"
              >
                {sortedMembers.length > 0 ? (
                  sortedMembers.map((sub) => (
                    <Member
                      key={sub?.id}
                      level={2}
                      onClick={handleSubaffiliateClick}
                      subAffiliate={sub ?? {}}
                    />
                  ))
                ) : (
                  <div className="py-5">{t('referrals.noSubaffiliates')}</div>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}

export default SubaffiliatesComponent;
